import React from 'react';
import { makeStyles, TextField, MenuItem } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import styled, { isStyledComponent, withTheme } from 'styled-components';
import PropTypes from 'prop-types';
import { Field } from 'formik';
import ReactTextMaskedInput from 'react-text-mask';

import { Flex, Box, Text } from 'Components/Base';
import { useConfig } from 'Context/ConfigContext';

const DEFAULT_INPUT_STYLE = {
  multiline: {
    padding: 0,
  },
  outlinedMultiLine: {
    padding: '0 0',
  },
  selectionPaper: { maxHeight: '320px' },
  selection: { padding: '0 32px 0 0 !important' },
  readOnly: {
    '& > fieldset': {
      borderColor: 'transparent !important',
    },
    '& > input': {
      color: 'black',
    },
    '& textarea': {
      color: 'black',
    },
  },
};

const getInputStyleByAppName = (uiVariant) => {
  if (!['PAAS', 'ANY'].includes(uiVariant)) {
    return {
      input: (theme) => ({
        boxSizing: 'border-box',
        padding: '12px 16px',
        '&.MuiOutlinedInput-inputMultiline': {
          padding: '18px 16px',
        },
        background: 'white',
        borderRadius: '12px !important',
        minHeight: '50px',
        fontFamily: theme?.fonts?.sans ?? 'sans-serif',
        '&::placeholder': {
          color: theme?.colors?.text.textBlack30,
        },
        '&:disabled': {
          background: theme?.colors?.grayscale.gray200,
        },
      }),
      outlinedLabel: (theme) => ({
        marginBottom: '8px',
        // Override MUI default label style
        position: 'relative',
        transform: 'translate(0, 0) scale(1) !important',
        color: theme?.colors?.text?.textBlack ?? '#3b3b3b',
        fontFamily: theme?.fonts?.sans ?? 'sans-serif',
        fontSize: '12px',
        fontWeight: 400,
        lineHeight: 1.5,
        '&.Mui-focused': {
          color: theme?.colors?.secondary,
        },
        '&.Mui-error': {
          color: theme?.colors?.error,
        },
      }),
      helperText: (theme) => ({
        margin: '8px 0px 0px',
        fontFamily: theme?.fonts?.sans ?? 'sans-serif',
        fontSize: theme?.fontSizes?.[0] ?? '12px',
        fontWeight: theme?.fontWeights?.medium ?? 500,
        lineHeight: theme?.lineHeights?.title ?? 1.25,
        '&.Mui-error': {
          color: theme?.colors?.error,
        },
      }),
      outlinedRoot: (theme) => ({
        '& > fieldset > legend': {
          display: 'none',
        },
        '&:hover .MuiOutlinedInput-notchedOutline ': {
          borderColor: theme?.colors?.secondary,
        },
        '&.Mui-disabled .MuiOutlinedInput-notchedOutline ': {
          border: 'none',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: theme?.colors?.secondary,
          borderWidth: '1px',
        },
        '&.Mui-error .MuiOutlinedInput-notchedOutline': {
          borderColor: theme?.colors?.error,
        },
      }),
      notchedOutline: (theme) => ({
        top: '0px',
        borderColor: theme?.colors?.greyscale.grey4,
        borderRadius: '12px',
      }),
      ...DEFAULT_INPUT_STYLE,
    };
  }

  return {
    input: (theme) => ({
      boxSizing: 'border-box',
      padding: '12px 21px',
      background: 'white',
      borderRadius: '4px !important',
      minHeight: '40px',
      fontFamily: theme?.fonts?.sans ?? 'sans-serif',
      // Workaround to override MUI hidden placeholder
      '&::placeholder': {
        opacity: '0.5 !important',
      },
    }),
    outlinedLabel: (theme) => ({
      marginBottom: '8px',
      // Override MUI default label style
      position: 'relative',
      transform: 'translate(0, 0) scale(1) !important',
      color: theme?.colors?.text ?? '#3b3b3b',
      fontFamily: theme?.fonts?.sans ?? 'sans-serif',
      fontSize: theme?.fontSizes?.[1] ?? '14px',
      fontWeight: theme?.fontWeights?.medium ?? 500,
      lineHeight: theme?.lineHeights?.title ?? 1.25,
    }),
    helperText: (theme) => ({
      margin: '8px 0px 0px',
      fontFamily: theme?.fonts?.sans ?? 'sans-serif',
      fontSize: theme?.fontSizes?.[0] ?? '12px',
      fontWeight: theme?.fontWeights?.medium ?? 500,
      lineHeight: theme?.lineHeights?.title ?? 1.25,
    }),
    outlinedRoot: {
      '& > fieldset > legend': {
        display: 'none',
      },
    },
    notchedOutline: {
      top: '0px',
    },
    ...DEFAULT_INPUT_STYLE,
  };
};

const useStyles = (props) => {
  const { uiVariant } = useConfig();
  return makeStyles(() => getInputStyleByAppName(uiVariant))(props);
};

const MaskedInput = ({ inputRef, maskInputProps = {}, startAdornment, endAdornment, ...props }) => {
  const {
    mask = (value) => Array.from(value, (element) => /./),
    guide = false,
    showMask = false,
  } = maskInputProps;
  return (
    <Flex alignItems={'center'} width={1}>
      {startAdornment &&
        React.isValidElement(startAdornment) &&
        React.cloneElement(startAdornment, { maxHeight: 30, ml: 2, mr: 2 })}
      <ReactTextMaskedInput
        {...props}
        mask={mask}
        guide={guide}
        showMask={showMask}
        ref={(ref) => {
          inputRef(ref ? ref.inputElement : null);
        }}
      />
      {endAdornment &&
        React.isValidElement(endAdornment) &&
        React.cloneElement(endAdornment, { maxHeight: 30, ml: 'auto', mr: 2 })}
    </Flex>
  );
};

MaskedInput.propTypes = {
  inputRef: PropTypes.func,
  maskInputProps: PropTypes.object,
  startAdornment: PropTypes.node,
  endAdornment: PropTypes.node,
};
MaskedInput.defaultProps = {
  maskInputProps: {},
  inputRef: {},
};

const Input = withTheme(
  ({
    theme,
    field,
    form,
    isMasked,
    maskInputProps,
    MuiInputProps,
    MuiInputLabelProps,
    inputProps,
    isFormik: isFormikProps,
    ...props
  }) => {
    const classes = useStyles(theme);

    const { onBlur: onFormikBlur, onChange: onFormikChange, name, value: formValue } = field || {};
    const isFormik = isFormikProps || typeof onFormikChange === 'function';

    const {
      error,
      label,
      disabled,
      readOnly,
      required,
      maxLength,
      minLength,
      helperText,
      onBlur = () => {},
      onChange = () => {},
      value,
      type,
      select,
    } = props ?? {};

    const handleBlur = isFormik ? onFormikBlur : onBlur;
    const handleChange = isFormik ? onFormikChange : onChange;
    const fieldError = isFormik ? form?.errors?.[name] : error;
    const showError = isFormik ? form?.touched?.[name] && !!fieldError : !!error;

    let inputValue = '';
    if (formValue) inputValue = formValue;
    else if (value === undefined) inputValue = undefined;
    else if (value) inputValue = value;

    return (
      <TextField
        {...props}
        SelectProps={
          select
            ? {
                displayEmpty: props?.placeholder ? true : false,
                classes: { root: classes.selection },
                MenuProps: {
                  getContentAnchorEl: null,
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'center',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'center',
                  },
                  marginThreshold: 0,
                  classes: {
                    paper: classes.selectionPaper,
                  },
                  MenuListProps: {
                    classes: { root: classes.menuItem },
                    disablePadding: true,
                  },
                },
              }
            : {}
        }
        name={name ?? props?.name}
        onBlur={handleBlur}
        onChange={handleChange}
        disabled={disabled || readOnly}
        label={label}
        error={showError}
        helperText={showError ? fieldError : helperText}
        FormHelperTextProps={{
          classes: { root: classes.helperText },
        }}
        variant="outlined"
        value={inputValue}
        required={required}
        inputProps={{
          maxLength,
          minLength,
          type: type ?? 'text',
          ...inputProps,
        }}
        InputLabelProps={{
          shrink: true,
          classes: {
            outlined: classes.outlinedLabel,
          },
          ...MuiInputLabelProps,
        }}
        InputProps={{
          classes: {
            root: readOnly ? classes.readOnly : classes.outlinedRoot,
            notchedOutline: classes.notchedOutline,
            input: classes.input,
            multiline: classes.outlinedMultiLine,
          },
          ...MuiInputProps,
          ...(isMasked
            ? {
                inputComponent: MaskedInput,
                inputProps: {
                  maxLength: maxLength,
                  minLength: minLength,
                  maskInputProps: maskInputProps,
                  ...props,
                },
                onBlur: handleBlur,
                onChange: handleChange,
              }
            : {}),
        }}
        fullWidth
      />
    );
  }
);

const FormTextInput = ({ name, as = Input, ...props }) => {
  return <Field name={name} component={as} {...props} />;
};

const TextMaskedInput = (props) => <Input {...props} isMasked={true} />;

const FormTextMaskedInput = ({ name, ...props }) => {
  return <Field name={name} component={Input} {...props} isMasked={true} />;
};

const TextArea = (props) => <Input {...props} />;

TextArea.propTypes = {
  rows: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  rowsMax: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  multiline: PropTypes.bool,
};
TextArea.defaultProps = {
  rows: 3,
  rowsMax: 3, //maxmimum rows on explaned,then scrollable
  multiline: true,
};

const FormTextArea = ({ name, ...props }) => {
  return <Field name={name} component={TextArea} {...props} />;
};

const useMenuItemStyle = makeStyles({
  menuItem: {
    minHeight: 0,
    padding: 0,
    '&.Mui-disabled': { opacity: 1 },
  },
});

const Selection = ({ options, placeholder, renderItem, ...props }) => {
  const classes = useMenuItemStyle();
  return (
    <Input placeholder={placeholder} {...props}>
      {placeholder && (
        <MenuItem value="" disabled classes={{ root: classes.menuItem }}>
          <Text textStyle={'body2'} py={3} px={'21px'} color={'#9d9d9d'}>
            {placeholder}
          </Text>
        </MenuItem>
      )}
      {Array.isArray(options) && options.length
        ? options.map((option, index) => (
            <MenuItem
              {...option}
              key={`${index}-${option.value}`}
              value={option.value}
              classes={{ root: classes.menuItem }}
            >
              {typeof renderItem === 'function' ? (
                React.cloneElement(renderItem({ ...option, index }))
              ) : (
                <div style={{ padding: '12px 21px', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                  {option.label}
                </div>
              )}
            </MenuItem>
          ))
        : null}
    </Input>
  );
};
Selection.propTypes = {
  select: PropTypes.bool,
  placeholder: PropTypes.string,
  renderItem: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })),
};
Selection.defaultProps = {
  select: true,
  options: [],
};
const FormSelection = ({ name, ...props }) => {
  return <Field name={name} component={Selection} {...props} />;
};

const HiddenRadioInput = styled.input`
  -webkit-appearance: none;
  -moz-appearance: none;
  -ms-appearance: none;
  -o-appearance: none;
  appearance: none;
  display: none;
`;

HiddenRadioInput.defaultProps = {
  type: 'radio',
};

const RadioOption = ({
  onChange,
  checked,
  name,
  label,
  onBlur,
  disabled,
  value,
  renderItem,
  index,
  ...props
}) => {
  const fieldHandler = {
    onChange,
    checked,
    name,
    label,
    onBlur,
    disabled,
    value,
    ...props,
  };
  if (typeof renderItem === 'function') {
    return (
      <Box as="label" {...props}>
        {React.cloneElement(
          renderItem({
            checked,
            name,
            label,
            onBlur,
            disabled,
            value,
            index,
            ...props,
          })
        )}
        <HiddenRadioInput
          checked={checked}
          name={name}
          onChange={onChange}
          onBlur={onBlur}
          value={value}
          disabled={disabled}
        />
      </Box>
    );
  }
  return (
    <>
      <input
        type="radio"
        style={{
          margin: '12px 21px',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          cursor: checked || disabled ? 'default' : 'pointer',
        }}
        {...fieldHandler}
      />
      {label}
    </>
  );
};
const optionPropTypes = {
  label: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  checked: PropTypes.bool,
  disabled: PropTypes.bool,
  renderItem: PropTypes.func,
  index: PropTypes.number,
};
RadioOption.propTypes = optionPropTypes;

const FormRadioOption = ({ label, name, value, index, ...props }) => (
  <Field name={name} type={'radio'} label={label} value={value} as={RadioOption} {...props} />
);
FormRadioOption.propTypes = optionPropTypes;

const DefaultContainer = (props) => <Flex alignItems="center" flexWrap="wrap" {...props} />;

const RadioInput = ({
  options,
  renderItem,
  label,
  name,
  labelProps,
  containerProps,
  optionContainer,
  optionContainerProps,
  isFormik,
  value,
  ...props
}) => {
  const OptionContainer =
    isStyledComponent(optionContainer) || typeof optionContainer === 'function'
      ? optionContainer
      : DefaultContainer;
  return (
    <Flex flexDirection="column" {...containerProps}>
      <Text {...labelProps}>{label}</Text>
      {Array.isArray(options) && options.length && (
        <OptionContainer {...optionContainerProps}>
          {options.map((option, index) => {
            return !isFormik ? (
              <RadioOption
                key={`${name}-${index}`}
                renderItem={renderItem}
                index={index}
                checked={value === option?.value}
                {...option}
                {...props}
              />
            ) : (
              <FormRadioOption
                key={`${name}-${index}`}
                name={name}
                index={index}
                renderItem={renderItem}
                {...option}
                {...props}
              />
            );
          })}
        </OptionContainer>
      )}
    </Flex>
  );
};
RadioInput.propTypes = {
  renderItem: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })),
  label: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.string,
  labelProps: PropTypes.object,
  containerProps: PropTypes.object,
  optionContainer: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  optionContainerProps: PropTypes.object,
  isFormik: PropTypes.bool,
};
const FormRadioInput = (props) => <RadioInput {...props} isFormik={true} />;

const HiddenCheckboxInput = styled.input`
  -webkit-appearance: none;
  -moz-appearance: none;
  -ms-appearance: none;
  -o-appearance: none;
  appearance: none;
  display: none;
`;

HiddenCheckboxInput.defaultProps = {
  type: 'checkbox',
};

const Checkbox = ({
  checked,
  disabled,
  onChange,
  onBlur,
  label,
  name,
  value,
  index,
  renderItem,
  ...props
}) => {
  const fieldHandler = {
    onChange,
    checked,
    name,
    label,
    onBlur,
    disabled,
    value,
    renderItem,
    index,
    ...props,
  };
  if (typeof renderItem === 'function') {
    return (
      <Box as="label" {...props}>
        {React.cloneElement(
          renderItem({
            checked,
            name,
            label,
            onBlur,
            disabled,
            value,
            index,
            ...props,
          })
        )}
        <HiddenCheckboxInput
          checked={checked}
          name={name}
          onChange={onChange}
          onBlur={onBlur}
          value={value}
          disabled={disabled}
        />
      </Box>
    );
  }
  return (
    <Flex flexDirection="row" alignItems="center">
      <input type="checkbox" {...fieldHandler} />
      <Text ml={1}>{label}</Text>
    </Flex>
  );
};

Checkbox.propTypes = optionPropTypes;

const FormCheckbox = ({ label, name, value, index, ...props }) => (
  <Field name={name} type={'checkbox'} label={label} value={value} as={Checkbox} {...props} />
);

FormCheckbox.propTypes = optionPropTypes;

const CheckboxInput = ({
  options,
  renderItem,
  label,
  name,
  labelProps,
  containerProps,
  optionContainer,
  optionContainerProps,
  isFormik,
  value,
  ...props
}) => {
  const OptionContainer =
    isStyledComponent(optionContainer) || typeof optionContainer === 'function'
      ? optionContainer
      : DefaultContainer;

  let values = [];
  if (Array.isArray(value) && Array.length) values = value;
  if (typeof value === 'string') values.push(value);

  return (
    <Flex flexDirection="column" {...containerProps}>
      <Text {...labelProps}>{label}</Text>
      {Array.isArray(options) && options.length && (
        <OptionContainer {...optionContainerProps}>
          {options.map((option, index) => {
            return !isFormik ? (
              <Checkbox
                key={`${name}-${index}`}
                renderItem={renderItem}
                index={index}
                checked={values.includes(option?.value)}
                {...option}
                {...props}
              />
            ) : (
              <FormCheckbox
                key={`${name}-${index}`}
                name={name}
                index={index}
                renderItem={renderItem}
                {...option}
                {...props}
              />
            );
          })}
        </OptionContainer>
      )}
    </Flex>
  );
};

CheckboxInput.propTypes = {
  renderItem: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })),
  label: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  labelProps: PropTypes.object,
  containerProps: PropTypes.object,
  optionContainer: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  optionContainerProps: PropTypes.object,
  isFormik: PropTypes.bool,
};
const FormCheckboxInput = (props) => <CheckboxInput {...props} isFormik={true} />;

const useAutoCompleteStyle = makeStyles({
  inputRoot: {
    '&.MuiAutocomplete-inputRoot': { padding: 0, background: 'white', borderRadius: '12px' },
    '&.MuiAutocomplete-inputRoot .MuiAutocomplete-input:first-child': { paddingLeft: '21px' },
    '&.MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
      padding: '12px 21px',
      '&:disabled': { background: 'transparent' },
    },
  },
});

const SearchSelectionInput = withTheme(
  ({
    theme,
    /**
     * @param {string[]} options array of string which search value will check equality
     */
    options,
    /**
     * @param {Object} field formik field props injected by formik context when component render under formik Field component
     */
    field,
    /**
     * @param {Object} form fomik utils injected by formik context when component render under formik Field component
     */
    form,
    /**
     * @param {string} label label to display top of input box, can be injected with or without formik context
     */
    label,
    onBlur = () => {},
    onChange = () => {},
    ...props
  }) => {
    const classes = useAutoCompleteStyle();
    const { onBlur: onFormikBlur, onChange: onFormikChange, value, name } = field || {};
    const isFormik = typeof onFormikChange === 'function' && typeof onFormikBlur === 'function';

    const handleBlur = isFormik
      ? (e, option) => onFormikBlur({ ...e, target: { name, value: option ?? '' } })
      : onBlur;
    const handleChange = isFormik
      ? (e, option) => onFormikChange({ ...e, target: { name, value: option ?? '' } })
      : onChange;

    return (
      <Autocomplete
        classes={classes}
        options={options}
        openOnFocus
        includeInputInList
        getOptionSelected={(option, value) => value === option}
        getOptionLabel={(option) => option}
        renderInput={(params) => (
          <Input
            MuiInputProps={params?.InputProps}
            inputProps={params?.inputProps}
            label={label}
            form={form}
            field={{ name }}
            isFormik={isFormik}
            MuiInputLabelProps={props?.MuiInputLabelProps ?? {}}
            error={props?.error}
            placeholder={props?.placeholder}
          />
        )}
        {...props}
        onChange={handleChange}
        onBlur={handleBlur}
        value={value || props.value || null}
      />
    );
  }
);

SearchSelectionInput.propTypes = {
  options: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])),
  isFormik: PropTypes.bool,
};

const FormSearchSelectionInput = (props) => <FormTextInput as={SearchSelectionInput} {...props} />;

export {
  FormTextMaskedInput,
  TextMaskedInput,
  FormTextInput,
  Input,
  TextArea,
  FormTextArea,
  Selection,
  FormSelection,
  SearchSelectionInput,
  FormSearchSelectionInput,
  RadioInput,
  FormRadioInput,
  CheckboxInput,
  FormCheckboxInput,
};
